import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { ReactComponent as CloseIcon } from '../../assets/icon-close.svg';
import { ReactComponent as DropdownArrowIcon } from '../../assets/icon-dropdown-arrow.svg';
import { ReactComponent as FilterDropdownIcon } from '../../assets/icon-filter-dropdown.svg';

const TemplateFilterDropdown = ({
  value,
  onChange,
  options,
  placeholder = 'Status',
  minWidth = '360px',
  maxWidth = '360px',
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setMenuIsOpen(false);
      }
    }

    if (menuIsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuIsOpen]);

  return (
    <div ref={selectRef}>
      <Select
        isMulti
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={options}
        components={{
          DropdownIndicator: () => <DropdownArrowIcon className="dropdown-arrow-icon" />,
          ValueContainer: ({ children, innerProps, innerRef, ...props }) => (
            <div ref={innerRef} {...innerProps} className="status-select__container">
              <FilterDropdownIcon className="status-select__icon" />
              {children}
            </div>
          ),
          MultiValueContainer: ({ children, innerProps, selectProps, data }) => {
            const selectedCount = selectProps.value.length;
            const index = selectProps.value.findIndex((val) => val.value === data.value);

            if (index > 0) {
              if (index === 1) {
                return (
                  <div {...innerProps} className="status-select__chip status-select__chip--more">
                    +{selectedCount - 1} more
                  </div>
                );
              }
              return null;
            }

            return (
              <div {...innerProps} className="status-select__chip">
                {children}
              </div>
            );
          },
          MultiValueLabel: (props) => {
            return (
              <div {...props.innerProps} className="status-select__chip-label">
                {props.children}
              </div>
            );
          },
          MultiValueRemove: (props) => {
            return (
              <div {...props.innerProps} className="status-select__chip-remove">
                <CloseIcon className="close-icon" />
              </div>
            );
          },
        }}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            minWidth,
            maxWidth,
            minHeight: '38px',
            borderColor: '#E2E8F0',
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: 'none',
            '&:hover': {
              borderColor: '#CBD5E0',
            },
            padding: '0 4px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 9999,
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: '#4A5568',
            fontWeight: 500,
            fontSize: '14px',
          }),
          option: (baseStyles, { isSelected }) => ({
            ...baseStyles,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: isSelected ? '#f8f9fa' : 'white',
            color: '#212529',
            ':hover': {
              backgroundColor: '#f8f9fa',
            },
            ':before': {
              content: '""',
              width: '16px',
              height: '16px',
              marginRight: '8px',
              border: `2px solid ${isSelected ? '#007bff' : '#dee2e6'}`,
              borderRadius: '3px',
              backgroundColor: isSelected ? '#007bff' : 'white',
              backgroundImage: isSelected
                ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="%23ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>')`
                : 'none',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '10px',
            },
          }),
          multiValue: () => ({
            display: 'flex',
            borderRadius: '6px',
            margin: '2px 4px',
            maxWidth: '100%',
            alignItems: 'center',
          }),
          multiValueLabel: () => ({
            display: 'block',
          }),
          multiValueRemove: () => ({
            display: 'none',
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '240px',
            '& > div': {
              overflow: 'hidden',
            },
          }),
        }}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
      />
    </div>
  );
};

TemplateFilterDropdown.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
};

export default TemplateFilterDropdown;
